// sort-imports-ignore
/* eslint-disable max-nested-callbacks */

import React, {useEffect, useRef, useState} from 'react';

import CSSModules from 'react-css-modules';
import PropTypes from 'prop-types';
import Delta from 'quill-delta';
import 'quill-paste-smart';
import ReactQuill from 'react-quill';

import TitleBlock from '../TitleBlock';
import {Toolbar} from '../Toolbar';

import colourStyles from '../../../../../resources/styles/colours.module.scss';
import 'react-quill/dist/quill.snow.css';
import styles from './styles.module.scss'; // must be imported after react-quill
import {sanitizeHtml} from '@edume/magnificent';

// Need to remove default icons for each toolbar button
// See https://github.com/zenoamaro/react-quill/issues/188
const icons = ReactQuill.Quill.import('ui/icons');
icons.bold = null;
icons.italic = null;
icons.underline = null;
icons.list = null;
icons.clean = null;

const NewRichTextEditor = ({
  value,
  onChangeValue,
  onFocus,
  onBlur,
  placeholder,
  validationStyles,
  title,
  isInvalid,
  optionalText,
  dataAutomation,
  readyStatusDataAutomation,
  showMarkdownLists = true,
}) => {
  const ref = useRef(null);

  const baseFormats = ['bold', 'italic', 'underline'];
  const formats = showMarkdownLists
    ? [...baseFormats, 'list', 'bullet']
    : baseFormats;

  useEffect(() => {
    if (ref.current && dataAutomation) {
      ref.current.editor?.root?.setAttribute('data-automation', dataAutomation);
    }
  }, [dataAutomation]);

  useEffect(() => {
    if (ref.current) {
      const keyboard = ref.current?.getEditor().getModule('keyboard');
      delete keyboard.bindings[9];

      // Strip out formatting that is not in the list of formats above
      ref.current
        .getEditor()
        .getModule('clipboard')
        .addMatcher(Node.ELEMENT_NODE, function (node, delta) {
          const newDelta = new Delta();
          // Work around needed due to a known issue with the library
          // https://github.com/zenoamaro/react-quill/issues/992
          if (node.style.fontWeight) {
            newDelta.insert(node.textContent, {
              ...delta.ops[0].attributes,
              bold: true,
            });
          } else {
            delta.ops.forEach((op) => {
              if (op.attributes) {
                const filteredAttributes = Object.keys(op.attributes)
                  .filter((attr) => formats.includes(attr))
                  .reduce((obj, key) => {
                    obj[key] = op.attributes[key];
                    return obj;
                  }, {});
                newDelta.insert(op.insert, filteredAttributes);
              } else {
                newDelta.insert(op.insert);
              }
            });
          }

          return newDelta;
        });
    }
  }, [ref, formats]);

  const [isFocused, setIsFocused] = useState(false);

  const handleOnFocus = () => {
    setIsFocused(true);
    if (onFocus) {
      onFocus();
    }
  };

  const handleOnBlur = () => {
    setIsFocused(false);
    if (onBlur) {
      onBlur();
    }
  };

  const modules = {
    toolbar: {
      container: '#toolbar',
    },
    clipboard: {
      matchVisual: false, // prevents </br> being inserted before lists
    },
  };

  return (
    <div
      styleName='container'
      style={{
        '--focus': isFocused
          ? colourStyles.brandPrimaryDark
          : colourStyles.grey300,
      }}
    >
      {title && (
        <TitleBlock
          title={title}
          optionalText={optionalText}
          isValid={!isInvalid}
          hasIcon={validationStyles === 'icon'}
          dataAutomation={readyStatusDataAutomation}
        />
      )}

      <div styleName='editor' dir='auto'>
        <Toolbar showMarkdownLists={showMarkdownLists} />
        <ReactQuill
          ref={ref}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          value={sanitizeHtml(value)}
          onChange={(v, delta, source) => {
            // prevent automatic sanitization from triggering unsavedChanges states when opening a published lesson
            // See [Initial onChange() event](https://github.com/zenoamaro/react-quill/issues/259)
            if (source === 'user') {
              // Quill.js ensures that the editor always has at least one block-level element (like <p><br></p>)
              // to maintain its structure but this is causing issues with our validation logic.
              const isEmpty = v === '<p><br></p>' || v.trim() === '';
              onChangeValue(isEmpty ? '' : v);
            }
          }}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
        />
      </div>
    </div>
  );
};

NewRichTextEditor.propTypes = {
  isInvalid: PropTypes.bool.isRequired,
  value: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  onChangeValue: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  title: PropTypes.string,
  validationStyles: PropTypes.oneOf(['normal', 'alwaysValid', 'hide', 'icon']),
  optionalText: PropTypes.string,
  dataAutomation: PropTypes.string,
  readyStatusDataAutomation: PropTypes.string,
  showMarkdownLists: PropTypes.bool,
};

export default CSSModules(NewRichTextEditor, styles);
